/**
 * useTheme hook — Phase 1a
 *
 * Single source of truth for theme preference and density. Manages:
 *   - Theme: 'light' | 'dark' | 'system' (system delegates to OS matchMedia)
 *   - Density: 'cozy' | 'compact' (persisted to localStorage)
 *
 * Effects are separated per concern (plan Section 6.3):
 *   1. Apply theme + persist to localStorage on pref change
 *   2. Apply density + persist to localStorage on density change
 *   3. Cross-tab sync via storage event (both keys)
 *   4. Live OS-theme tracking — only when pref === 'system'
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import type { ThemePreference, ResolvedTheme, Density } from '../types/theme.js';
import {
	readStoredPreference,
	resolveTheme,
	applyTheme,
	readStoredDensity,
	applyDensity,
} from '../lib/theme.js';

export function useTheme() {
	const [pref, setPref] = useState<ThemePreference>(() => readStoredPreference());
	const [resolved, setResolved] = useState<ResolvedTheme>(() =>
		resolveTheme(readStoredPreference()),
	);
	const [density, setDensityState] = useState<Density>(() => readStoredDensity());

	// Keep a ref to the current pref so the OS-change callback can guard against
	// stale closures — particularly in test environments where the matchMedia
	// stub's removeEventListener is a no-op and does not actually remove the
	// registered listener. The guard ensures OS-flip events are ignored once
	// the user has switched away from 'system' mode.
	const prefRef = useRef<ThemePreference>(pref);
	useEffect(() => {
		prefRef.current = pref;
	}, [pref]);

	// Effect 1 — apply theme and persist on pref change
	useEffect(() => {
		const r = resolveTheme(pref);
		setResolved(r);
		applyTheme(r);
		try {
			localStorage.setItem('pia-theme', pref);
		} catch {
			/* Storage unavailable — ignore; DOM attribute is still applied */
		}
	}, [pref]);

	// Effect 2 — apply density and persist on density change
	useEffect(() => {
		applyDensity(density);
		try {
			localStorage.setItem('pia-density', density);
		} catch {
			/* Storage unavailable — ignore */
		}
	}, [density]);

	// Effect 3 — cross-tab sync for both theme and density keys
	useEffect(() => {
		const onStorage = (e: StorageEvent) => {
			if (e.key === 'pia-theme') {
				setPref(readStoredPreference());
			}
			if (e.key === 'pia-density') {
				setDensityState(readStoredDensity());
			}
		};
		window.addEventListener('storage', onStorage);
		return () => window.removeEventListener('storage', onStorage);
	}, []);

	// Effect 4 — live OS-theme tracking, only when pref === 'system'.
	// The callback also checks prefRef.current as a secondary guard: if the
	// matchMedia stub in tests does not honour removeEventListener, the stale
	// callback will still be a no-op because pref is no longer 'system'.
	useEffect(() => {
		if (pref !== 'system') return;

		const mql = window.matchMedia('(prefers-color-scheme: dark)');

		const onChange = () => {
			// Guard against stale listener firing after pref has changed away from 'system'
			if (prefRef.current !== 'system') return;
			const r: ResolvedTheme = mql.matches ? 'dark' : 'light';
			setResolved(r);
			applyTheme(r);
		};

		mql.addEventListener('change', onChange);
		return () => mql.removeEventListener('change', onChange);
	}, [pref]);

	const setPreference = useCallback((p: ThemePreference) => setPref(p), []);
	const setDensity = useCallback((d: Density) => setDensityState(d), []);

	return { preference: pref, resolved, setPreference, density, setDensity };
}
